<template>
  <div>
    <!-- Dialog to change parameters -->
    <v-dialog v-model="displayParameters" hide-overlay width="600px">
      <v-card>
        <v-card-title>Multiple Alignment Layout</v-card-title>

        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <v-card>
                <v-card-title>Coloration</v-card-title>
                <v-card-text>
                  <v-radio-group v-model="coloring">
                    <v-radio label="By metadata" value="metadata"></v-radio>
                    <v-radio label="By nucleotide type" value="auto"></v-radio>
                    <v-radio
                      label="By group (disables collapsing)"
                      value="seqcolor"
                      :disabled="radiobuttonDistanceColorDisabled"
                    ></v-radio>
                    <v-radio label="No coloring" value="no"></v-radio>
                  </v-radio-group>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeParameters()"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- dialog for help message -->
    <v-dialog v-model="showHelp" persistent width="600px">
      <v-card>
        <v-card-title>Multiple alignement panel legend</v-card-title>
        <v-card-text>
          Correspondence between colors and sequence conservation levels:
          <v-row v-for="c in getConservationCategories" :key="c.label" no-gutters>
            <v-col>
              <v-btn x-small rounded :color="c.style.fill" class="metadata-btn"></v-btn>
              {{ c.label }}</v-col
            >
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeHelpEvent()"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- horizontal slider -->
    <vue-slider
      v-if="maxPos < maxLengthSeqs"
      v-model="rangeSlider"
      :max="maxLengthSeqs"
      :min="0"
      :min-range="maxPos"
      :max-range="maxPos"
      :fixed="true"
      :use-keyboard="true"
      lazy
      @change="moveSlider"
    >
      <template v-slot:dot="{ value, focus }">
        <div :class="['custom-dot', { focus }]"></div>
      </template>
    </vue-slider>
    <!-- Vertical slider -->
    <vue-slider
      v-if="displaySliderSequences"
      v-model="rangeVerticalSlider"
      :min="0"
      :max="allSeqs.length - 1"
      :min-range="nbDisplayedSequences"
      :max-range="nbDisplayedSequences"
      :fixed="true"
      :use-keyboard="true"
      direction="ttb"
      :enable-cross="false"
      class="verticalSlider"
      :height="heightVerticalSlider"
      :style="cssVars"
      :tooltip-formatter="tooltipFormatter"
      :tooltip-placement="['top', 'bottom']"
      lazy
    >
      <template v-slot:dot="{ value, focus }">
        <div :class="['vertical-custom-dot', { focus }]"></div>
      </template>
    </vue-slider>
    <svg-msa
      :start="rangeSlider[0] + 1"
      :end="rangeSlider[1] + 1"
      :seqs="seqs"
      :tracks="tracks"
      :selectedseqs="selectedNodeIds"
      :coloring="coloring"
      :metadata="selectedMetadataOnSelectedSequences"
      :glyphs="glyphs"
      :display-glyph-tooltip="true"
      :display-track-tooltip="false"
      :offset-x="210"
      :highlight-selection="highlightSelection"
      v-on="$listeners"
    ></svg-msa>
  </div>
</template>

<script>
import SeqTree from '@/tree.js';
import SeqMetadata from '@/metadata.js';

import { displayParameters } from '@/mixins/displayParameters.js';

import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';
import { SvgMsa } from 'vue-svg-msa';
import 'vue-svg-msa/dist/vue-svg-msa.css';

export default {
  components: {
    VueSlider,
    SvgMsa,
  },
  mixins: [displayParameters],
  props: {
    tree: {
      type: SeqTree,
      default: function() {
        return new SeqTree();
      },
    },
    tracks: {
      type: Array,
      default: function() {
        return [];
      },
    },
    metadata: {
      type: SeqMetadata,
      default: function() {
        return new SeqMetadata();
      },
    },
    glyphs: {
      type: Array,
      default: function() {
        return [];
      },
    },
    currentId: {
      type: String,
      default: '',
    },
    maxPos: {
      type: Number,
      default: 160,
    },
    showHelp: { type: Boolean, default: false },
    start: { type: Number, default: 0 },
    end: { type: Number, default: -1 },
    /**
     * array of LEAVE ids which will be highlight in the msa:
     * Can not be used directly: have to look for the node id including these leave
     */
    selectedIds: {
      type: Array,
      default: function() {
        return [];
      },
    },
    minDist: { type: Number, default: 0 },
    highlightSelection: {
      type: String,
      default: 'undefined',
      validator: function(value) {
        return ['undefined', 'always', 'never'].indexOf(value) !== -1;
      },
    },
  },
  data() {
    return {
      currentNodeId: this.currentId,
      currentDepth: 6,
      lastDisplayedSeqIndex: 35, // not used
      coloring: 'metadata',
      selectedLeafIds: this.selectedIds,
      radiobuttonDistanceColorDisabled: false,
      rangeSliderProxy: null,
      rangeVerticalSliderProxy: null,
      /*
      Number of the first sequence to display
      */
      startVerticalDisplay: 0,
      /*
      Number of sequences to display
      */
      nbDisplayedSequences: 20,
      /*
      Formatter for the tooltip of the vertical slider
      */
      tooltipFormatter: v => `seq ${v}`,
    };
  },
  computed: {
    /**
     * Max length of the seqs
     */
    maxLengthSeqs() {
      return Math.max(...this.allSeqs.map(s => s.seq.length));
    },
    /**
     * Range for the horizontal slider
     */
    rangeSlider: {
      get: function() {
        let begin = this.start;
        let end = this.end;
        let a_pos = [begin, end];
        return this.rangeSliderProxy == null ? a_pos : this.rangeSliderProxy;
      },
      set: function(newValue) {
        this.rangeSliderProxy = newValue;
      },
    },
    /**
     * Range for the vertical slider
     */
    rangeVerticalSlider: {
      get: function() {
        let begin = this.startVerticalDisplay;
        let end = this.startVerticalDisplay + this.nbDisplayedSequences;
        if (end >= this.allSeqs.length) {
          end = this.allSeqs.length - 1;
        }
        let a_pos = [begin, end];
        return this.rangeVerticalSliderProxy == null ? a_pos : this.rangeVerticalSliderProxy;
      },
      set: function(newValue) {
        this.rangeVerticalSliderProxy = newValue;
      },
    },
    /**
     * Height of the vertical slider
     */
    heightVerticalSlider() {
      return this.seqs.length * 15;
    },
    /**
     * css variables :
     * -vslider-top : position of the vertical slider
     */
    cssVars() {
      return { '--vslider-top': 115 + this.tracks.length * 20 + 'px' };
    },

    /**
     * Compute the node ids to select
     * Search the nodes which include at least one selected leaf id
     */
    selectedNodeIds() {
      return this.seqs
        .filter(s => !(s.isConsensus == true && s.isNode == false))
        .map(s =>
          this.tree.getLeaveIds(s.id).some(i => this.selectedLeafIds.includes(i)) ? s.id : ''
        )
        .filter(v => v !== '');
    },
    /**
     * Return an array of sequence objects
     * The returned sequences change according to the selectedNode and the minimum distance choosen for collapsing
     */
    allSeqs() {
      // if colorInsteadOfCollapse=true, the node with a min distance are colored, not collapsed
      const colorInsteadOfCollapse = this.coloring === 'seqcolor' ? true : false;

      let seqs = this.tree.getSequencesWithMinDistAndRenameConsensus(
        this.currentNodeId,
        this.minDist,
        colorInsteadOfCollapse,
        this.selectedLeafIds
      );

      return seqs;
    },

    /**
     * Selection of seqs to display
     */
    seqs() {
      let seqs = this.allSeqs;

      let startVerticalDisplay = this.rangeVerticalSlider[0];

      let endDisplay = this.rangeVerticalSlider[1];

      let displayedSeqs = seqs.slice(startVerticalDisplay, endDisplay);

      if (endDisplay < seqs.length) displayedSeqs.push(this.consensus);

      return displayedSeqs;
    },

    /**
     * Return the consensus sequence object
     */
    consensus() {
      return this.allSeqs.find(s => s.isConsensus && s.isNode == false);
    },
    /**
     * Return an array of metadata with type "conservation"
     */
    getConservationCategories() {
      return this.metadata.getMetadataByType(['conservation']).flatMap(m => m.categories);
    },
    /**
     * Build an array of metadata
     * Computed for all the sequences
     * TODO: move this function to the parent component
     */
    selectedMetadata() {
      if (this.allSeqs) {
        let convertedMetadata = [];

        const selectedMetadataConservation = this.metadata
          .getMetadataFromId(this.currentNodeId)
          .filter(mm => mm.hasOwnProperty('trackType') && mm.trackType === 'conservation');

        let metadataConservation = this.metadata.extractMetadata(
          selectedMetadataConservation,
          this.consensus,
          this.allSeqs
        );

        // Ce bout de code fait pour projeter des data sur chaque sequence
        // fait coincer l'appli
        // A changer si on veut remettre ce feature

        // this.allSeqs.forEach(s => {
        //   let metadataNoConservation = this.metadata
        //     .getMetadataFromId(s.id)
        //     // .getMetadataFromId(this.currentNodeId)
        //     .filter(
        //       mm =>
        //         mm.hasOwnProperty('trackType') &&
        //         mm.trackType !== 'conservation' &&
        //         mm.trackType !== 'genotype'
        //     );
        //   convertedMetadata.push(...metadataNoConservation);
        // });

        let metadataNoConservationAll = this.metadata
          .getMetadataFromId('_all')
          .filter(
            mm =>
              mm.hasOwnProperty('trackType') &&
              mm.trackType !== 'conservation' &&
              mm.trackType !== 'genotype'
          );
        convertedMetadata.push(...metadataNoConservationAll);

        convertedMetadata.push(...metadataConservation);
        //this.metadata.printMetadata(a);

        return convertedMetadata;
      } else {
        return [];
      }
    },
    /**
     * Metadata only for the displayed sequences
     */
    selectedMetadataOnSelectedSequences() {
      return this.selectedMetadata.map(m => {
        let newM = Object.assign({}, m);

        newM.categories = m.categories.map(c => {
          let newC = Object.assign({}, c);
          newC.regions = c.regions.filter(r => {
            return this.seqs.some(s => s.id === r.id);
          });
          return newC;
        });
        return newM;
      });
    },
    /**
     * True if the vertical slider has to be displayed
     */
    displaySliderSequences() {
      return this.seqs.length < this.nbDisplayedSequences ? false : true;
    },
  },
  watch: {
    currentId(v) {
      this.currentNodeId = v;
    },
    start(v) {
      this.rangeSlider[0] = v;
    },
    selectedIds(v) {
      this.selectedLeafIds = v;
    },
    minDist() {
      this.rangeVerticalSliderProxy = null;
      this.startVerticalDisplay = 0;
    },
  },
  methods: {
    /**
     * Update the current node ID and emit its value
     */
    updateCurrentNodeId(newId) {
      this.currentNodeId = newId;
      this.$emit('select-node', [this.currentNodeId]);
    },
    closeHelpEvent() {
      this.$emit('hideHelp');
    },
    moveSlider(pos) {
      this.$emit('extract', pos);
    },
  },
};
</script>
<style scoped>
.metadata-btn {
  pointer-events: none;
  opacity: 0.4;
}

.vue-slider {
  margin: 12px;
}

.custom-dot {
  width: 50%;
  height: 100%;
  border-radius: 0;
  background-color: #3498db;
  transition: all 0.1s;
}
.custom-dot:hover {
  width: 100%;
}

.vertical-custom-dot {
  width: 100%;
  height: 50%;
  border-radius: 0;
  background-color: #3498db;
  transition: all 0.1s;
}

.verticalSlider {
  position: absolute;
  left: -15px;
  top: var(--vslider-top);
}
</style>
